import { useMutation, useQuery } from '@tanstack/react-query';
import { useLocalSearchParams } from 'expo-router';
import { useRef } from 'react';

import { Header } from '@/components/header';
import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { Link, LinkText } from '@/components/ui/link';
import { LoaderButton } from '@/components/ui/loader-button';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { PRIVACY_POLICY_URL, TERMS_OF_USE_URL } from '@/constants/links';
import {
  acceptEmailConsent,
  classifyEmailConsentFailure,
  getEmailConsentQueryOptions,
} from '@/services/emailConsent';

type MessageProps = {
  heading: string;
  body: string;
  testID: string;
};

const Message = ({ heading, body, testID }: MessageProps) => (
  <VStack testID={testID} className="items-center gap-3" accessibilityLiveRegion="polite">
    <Heading size="h2-md" className="text-center">
      {heading}
    </Heading>

    <Text className="text-center text-typography-700">{body}</Text>
  </VStack>
);

/**
 * Landing page for the consent link in an auto-reply email.
 *
 * The token in the path is the whole grant, so this renders for anyone holding it, signed in
 * or not, and the route is on the list a signed-in reader is left on. Loading the page only
 * reads the request. Nothing is recorded until the reader presses the button, because mail
 * scanners and link previewers open links and must never be able to accept on someone's
 * behalf.
 *
 * Acceptance is idempotent on the server, so the done state is driven by the mutation result
 * or by a request that was already accepted when the page loaded, and a second press can
 * never report a failure.
 */
export function EmailConsentPage() {
  const { token } = useLocalSearchParams<{ token: string }>();

  const consentQuery = useQuery(getEmailConsentQueryOptions({ token }));

  const isSubmitting = useRef(false);

  const acceptMutation = useMutation({
    mutationFn: () => acceptEmailConsent({ token }),
    onSettled: () => {
      isSubmitting.current = false;
    },
  });

  const accept = () => {
    if (isSubmitting.current) {
      return;
    }

    isSubmitting.current = true;

    acceptMutation.mutate();
  };

  const consent = consentQuery.data;

  const loadFailure =
    consentQuery.error && !consent ? classifyEmailConsentFailure(consentQuery.error) : null;

  const acceptFailure = acceptMutation.error
    ? classifyEmailConsentFailure(acceptMutation.error)
    : null;

  const renderBody = () => {
    if (consentQuery.isPending && consentQuery.fetchStatus !== 'idle') {
      return <Spinner testID="email-consent-loading" />;
    }

    if (loadFailure === 'rate-limited') {
      return (
        <Message
          testID="email-consent-rate-limited"
          heading="Please try again in a moment"
          body="We're getting a lot of requests right now. Wait a minute, then reload this page."
        />
      );
    }

    if (loadFailure === 'not-found' || (!consent && !loadFailure)) {
      return (
        <Message
          testID="email-consent-not-found"
          heading="We couldn't find this link"
          body="It may have been copied incorrectly. Open the link from your email again, or reply to that email and we'll send a new one."
        />
      );
    }

    if (!consent) {
      return (
        <Message
          testID="email-consent-error"
          heading="Something went wrong"
          body="We couldn't load this page. Reload it, or try again in a few minutes."
        />
      );
    }

    if (acceptMutation.isSuccess || consent.status === 'accepted') {
      return (
        <Message
          testID="email-consent-done"
          heading="You're all set"
          body="Thank you. You agreed to Primum's Terms of Service, and you can go back to your conversation with us."
        />
      );
    }

    if (consent.status === 'expired' || acceptFailure === 'expired') {
      return (
        <Message
          testID="email-consent-expired"
          heading="This link has expired"
          body="Reply to the email you received and we'll send you a new link."
        />
      );
    }

    return (
      <VStack testID="email-consent-prompt" className="w-full gap-6">
        <VStack className="gap-3">
          <Heading size="h2-md" className="text-center">
            Agree to continue
          </Heading>

          <Text className="text-center text-typography-700">
            By agreeing, you confirm that the messages you send to Primum will not contain
            patient-identifiable information (PHI), and you agree to Primum's{' '}
            <Link isExternal href={TERMS_OF_USE_URL}>
              <LinkText className="text-typography-900 underline">Terms of Service</LinkText>
            </Link>{' '}
            and{' '}
            <Link isExternal href={PRIVACY_POLICY_URL}>
              <LinkText className="text-typography-900 underline">Privacy Policy</LinkText>
            </Link>
            .
          </Text>
        </VStack>

        <LoaderButton
          action="primary"
          size="lg"
          isLoading={acceptMutation.isPending}
          onPress={accept}
          aria-label={acceptMutation.isPending ? 'Recording your agreement' : 'I agree'}>
          I agree
        </LoaderButton>

        {acceptFailure ? (
          <VStack className="items-center gap-2" accessibilityLiveRegion="assertive">
            <Text size="sm" className="text-center text-error-700" testID="email-consent-retry">
              {acceptFailure === 'rate-limited'
                ? "We're getting a lot of requests right now. Wait a minute, then press I agree again."
                : "We couldn't record your agreement. Press I agree to try again."}
            </Text>
          </VStack>
        ) : null}
      </VStack>
    );
  };

  return (
    <>
      <Header title="Terms of Service" />

      <VStack className="w-full flex-1 items-center justify-center px-5 py-10 web:mx-auto web:max-w-screen-sm">
        {renderBody()}

        {loadFailure === 'unknown' ? (
          <Button variant="outline" className="mt-6" onPress={() => consentQuery.refetch()}>
            <ButtonText>Try again</ButtonText>
          </Button>
        ) : null}
      </VStack>
    </>
  );
}
